import axios from 'axios';
import { addAuthHeaders, isTokenExpired, refreshExpiredToken } from './token';
import { AuthService, setAuthenticating } from '../../features/auth';
import { store } from '../store';
import { AuthUtils } from '../../common/utils';

export function createHttpClient(serverUrl: string) {
  const errorMessage = 'Token is invalid. Application needs to reauthenticate.';
  let inProgress = false;
  let retry = false;

  const axiosInstance = axios.create({
    baseURL: serverUrl,
  });

  const fixTokenIssue = () => {
    store.dispatch(setAuthenticating(true, errorMessage));
    AuthUtils.clearAuth();
    AuthService.fetchCode();
  }

  const fixIfRefreshTokenIssue = (e: any) => {
    if (axios.isAxiosError(e) && e.response && e.response.status === 400 && e.response.data?.error === 'invalid_grant') {              
      fixTokenIssue();
    }
  }

  axiosInstance.interceptors.request.use(
    async config => {
      if (isTokenExpired() && !inProgress) {
        try {
          inProgress = true;
          await refreshExpiredToken();
          inProgress = false;
        } catch (e) {
          fixIfRefreshTokenIssue(e);
          return {
            ...config,
            cancelToken: new axios.CancelToken((cancel) => 
              cancel(errorMessage)
            )
          };
        }
      }
      return addAuthHeaders(config);
    },
    error => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    response => response,
    async error => {
      const { config } = error;
      /* eslint-disable no-underscore-dangle */
      if (
        error.response?.data.status === 401 &&
        error.response?.data.message?.includes('The Token has expired')
      ) {
        if (!inProgress) {
          inProgress = true;
          try {
            if (!retry) {
              retry = true;
              await refreshExpiredToken();
              retry = false;
            } else {
              error.response.data.message = errorMessage;
              fixTokenIssue();
              return Promise.reject(error);
            }
          } catch (e) {
            error.response.data.message = errorMessage;
            fixIfRefreshTokenIssue(e);
            return Promise.reject(error);
          } finally {
            inProgress = false;
          }
          return axiosInstance(addAuthHeaders(config));
        } else {
          error.response.data.message = errorMessage;
        }
      }
        
      /* eslint-enable no-underscore-dangle */
      return Promise.reject(error);
    }
  );

  return axiosInstance;
}
